/**
 * BeneficialOwnerSection - TASK-376
 * Reusable beneficial owner list component with add/remove functionality.
 * Used by both ClientProfileForm and FormAForm.
 */

import React from 'react';
import { Plus, Trash2, UserCheck } from 'lucide-react';
import { InheritedBadge } from './InheritedBadge';

export interface BeneficialOwnerEntry {
  name: string;
  date_of_birth: string;
  nationality: string;
  address: string;
  ownership_percentage: number;
  source?: string;
}

interface BeneficialOwnerSectionProps {
  owners: BeneficialOwnerEntry[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: keyof BeneficialOwnerEntry, value: string | number) => void;
  readOnly?: boolean;
  maxOwners?: number;
}

export const BeneficialOwnerSection: React.FC<BeneficialOwnerSectionProps> = ({
  owners,
  onAdd,
  onRemove,
  onChange,
  readOnly = false,
  maxOwners = 10,
}) => {
  const totalPercentage = owners.reduce(
    (sum, owner) => sum + (owner.ownership_percentage || 0),
    0
  );
  const percentageValid = totalPercentage <= 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">
            Beneficial Owners
          </h4>
          <span className="text-xs text-gray-500">
            ({owners.length} {owners.length === 1 ? 'owner' : 'owners'})
          </span>
        </div>
        {!readOnly && owners.length < maxOwners && (
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Owner
          </button>
        )}
      </div>

      {/* Total percentage indicator */}
      {owners.length > 0 && (
        <div className={`text-xs px-3 py-1.5 rounded ${
          percentageValid
            ? 'bg-gray-50 text-gray-600'
            : 'bg-red-50 text-red-600'
        }`}>
          Total ownership: {totalPercentage}%
          {!percentageValid && ' (exceeds 100%)'}
        </div>
      )}

      {owners.length === 0 && (
        <div className="text-center py-6 border border-dashed border-gray-300 rounded-lg">
          <UserCheck className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No beneficial owners added</p>
          {!readOnly && (
            <button
              type="button"
              onClick={onAdd}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              Add first beneficial owner
            </button>
          )}
        </div>
      )}

      {owners.map((owner, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg p-4 space-y-3 bg-white"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Owner {index + 1}
            </span>
            <div className="flex items-center gap-2">
              {owner.source === 'inherited' && <InheritedBadge />}
              {!readOnly && owners.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Remove owner"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={owner.name || ''}
                onChange={(e) => onChange(index, 'name', e.target.value)}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 read-only:bg-gray-50"
                placeholder="Full legal name"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Date of Birth *
              </label>
              <input
                type="date"
                value={owner.date_of_birth || ''}
                onChange={(e) => onChange(index, 'date_of_birth', e.target.value)}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 read-only:bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Nationality *
              </label>
              <input
                type="text"
                value={owner.nationality || ''}
                onChange={(e) => onChange(index, 'nationality', e.target.value)}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 read-only:bg-gray-50"
                placeholder="e.g. Swiss"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Ownership % *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={owner.ownership_percentage ?? ''}
                onChange={(e) => onChange(index, 'ownership_percentage', parseFloat(e.target.value) || 0)}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 read-only:bg-gray-50"
                placeholder="100"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Address *
            </label>
            <input
              type="text"
              value={owner.address || ''}
              onChange={(e) => onChange(index, 'address', e.target.value)}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 read-only:bg-gray-50"
              placeholder="Full residential address"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default BeneficialOwnerSection;
